import { useState, useCallback } from 'react';
import { useSignUp, useUser, useClerk } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';

import { markCodeSent } from '@/components/verification/verificationSendFlag';

/**
 * Best-effort: kick off the deferred email verification (NEA-5773) right after
 * sign-up so the first code is already waiting when the user reaches the
 * dashboard. The Unify Clerk app doesn't verify at sign-up, so the new user's
 * primary email is unverified — `prepareVerification` emails them a code. This
 * is fire-and-forget; if it fails (e.g. the user resource isn't ready yet), the
 * dashboard's VerifyEmailDialog auto-sends on open instead. The session flag
 * stops the dialog double-sending when this succeeds.
 */
async function triggerEmailVerification() {
	try {
		await window.Clerk?.user?.reload();
		const emailAddress = window.Clerk?.user?.primaryEmailAddress;
		if (!emailAddress || emailAddress.verification?.status === 'verified') return;
		await emailAddress.prepareVerification({ strategy: 'email_code' });
		markCodeSent(emailAddress.emailAddress);
	} catch {
		// Swallowed — the dashboard banner/dialog is the fallback path.
	}
}

export const useAuthentication = () => {
	const { signUp, isLoaded: isSignUpLoaded, setActive } = useSignUp();
	const { isSignedIn, isLoaded: isUserLoaded } = useUser();
	const clerk = useClerk();

	const [isAuthenticating, setIsAuthenticating] = useState(false);
	const [authError, setAuthError] = useState(null);

	// The Unify Clerk instance runs in single-session mode, so `signUp.create()`
	// throws `session_exists` whenever the browser's Clerk client still holds a
	// session. We only get here when `useUser()` reports signed out, so this is
	// narrow cover for a client that disagrees with that — e.g. a cross-tab sync
	// race. Note `useUser().isSignedIn` is `Boolean(clerk.user)`, and `clerk.user`
	// comes from `signedInSessions`, which INCLUDES pending: an org-less pending
	// session reads as signed in here and never reaches this branch. (It reads as
	// signed out from `useAuth()`/`<SignedOut>`, which key off `Clerk.isSignedIn`
	// — that's the divergence NEA-5953 documents.)
	//
	// `signOut()` must be passed the no-op callback: the no-argument form
	// navigates to `buildAfterSignOutUrl()` (`/`, which PrivateRoute bounces to
	// `/login`), which would unload the page mid-retry (NEA-6503).
	const withStaleSessionRetry = useCallback(
		async (startSignUp) => {
			try {
				return await startSignUp();
			} catch (err) {
				if (err.errors?.[0]?.code !== 'session_exists') throw err;
				await clerk.signOut(() => {});
				return startSignUp();
			}
		},
		[clerk]
	);

	const handleEmailPasswordSignUp = useCallback(
		async (email, password, fullName) => {
			if (!isSignUpLoaded) {
				// Don't fail silently — the submit button is live before Clerk
				// finishes loading, and a bare `{ success: false }` used to leave
				// the user staring at an unchanged form with no explanation.
				setAuthError('We could not reach our sign-up service. Please refresh the page and try again.');
				return { success: false, error: 'Clerk sign-up not loaded' };
			}

			try {
				setIsAuthenticating(true);
				setAuthError(null);

				// Split full name into first and last name
				const nameParts = fullName.trim().split(' ');
				const firstName = nameParts[0] || '';
				const lastName = nameParts.slice(1).join(' ') || '';

				// Create the sign-up
				const result = await withStaleSessionRetry(() =>
					signUp.create({
						emailAddress: email,
						password,
						firstName,
						lastName,
						unsafeMetadata: {
							product: 'unify'
						}
					})
				);

				// The Unify Clerk app doesn't require email verification at
				// sign-up, so a password sign-up completes immediately and Clerk
				// returns a session — activate it so the user is logged in and
				// onboarding continues inline as that user (NEA-5767). If Clerk
				// returns an incomplete sign-up (e.g. verification got re-enabled)
				// there's no session to activate, so surface it rather than
				// silently leaving the user signed out.
				if (result.status !== 'complete') {
					setIsAuthenticating(false);
					const reason = `Unexpected sign-up status: ${result.status}`;
					Sentry.captureException(new Error(reason), {
						tags: { feature: 'unify-onboarding', signUpOutcome: 'incomplete' },
						extra: {
							status: result.status,
							missingFields: result.missingFields,
							unverifiedFields: result.unverifiedFields
						}
					});
					setAuthError('Something went wrong creating your account. Please try again or contact support@near.st.');
					return { success: false, error: reason, userId: result.createdUserId };
				}

				await setActive({ session: result.createdSessionId });

				// Fire-and-forget — don't block sign-up on the verification email.
				triggerEmailVerification();

				setIsAuthenticating(false);
				return { success: true, userId: result.createdUserId };
			} catch (err) {
				console.error('Email/password sign-up error:', err);
				setIsAuthenticating(false);

				// Until NEA-6503 this was console-only, so a failed sign-up left no
				// trace anywhere and reports of "signup is broken" couldn't be
				// diagnosed. Expected user-correctable codes (e.g.
				// form_identifier_exists) can be filtered out in Sentry on the
				// clerkErrorCode tag.
				Sentry.captureException(err, {
					tags: {
						feature: 'unify-onboarding',
						signUpOutcome: 'error',
						clerkErrorCode: err.errors?.[0]?.code || 'unknown'
					},
					extra: { clerkErrorMessage: err.errors?.[0]?.message }
				});

				// Handle specific Clerk errors
				if (err.errors?.[0]?.code === 'form_identifier_exists') {
					setAuthError('This email is already registered. Please sign in instead.');
				} else if (err.errors?.[0]?.code === 'form_password_pwned') {
					setAuthError('This password has been found in a data breach. Please choose a different password.');
				} else if (err.errors?.[0]?.code === 'form_password_length_too_short') {
					setAuthError('Password must be at least 8 characters.');
				} else if (err.errors?.[0]?.message) {
					setAuthError(err.errors[0].message);
				} else {
					setAuthError('Sign-up failed. Please try again.');
				}

				return { success: false, error: err.errors?.[0]?.message || 'Sign-up failed' };
			}
		},
		[signUp, isSignUpLoaded, setActive, withStaleSessionRetry]
	);

	const clearAuthError = useCallback(() => {
		setAuthError(null);
	}, []);

	return {
		// State
		isSignedIn,
		isUserLoaded,
		isAuthenticating,
		authError,

		// Methods

		handleEmailPasswordSignUp,
		clearAuthError
	};
};
